/**
 * SPEC.md §3, §11 — the draggable splitter between Source and Preview.
 *
 * Issue #5 (M2.6). Self-contained: it only reports a ratio change, App.tsx
 * owns the ratio's state and persistence. `role="separator"` with arrow-key
 * resize per §11's accessibility bar.
 */
import { useRef } from 'react';

export const MIN_RATIO = 0.2;
export const MAX_RATIO = 0.8;

const KEYBOARD_STEP = 0.02;

interface SplitterProps {
  ratio: number;
  onChange: (ratio: number) => void;
}

export default function Splitter({ ratio, onChange }: SplitterProps) {
  // A ref, not e.currentTarget.hasPointerCapture(...) — the same "am I mid-
  // drag" idiom Preview.tsx's pan handling already uses, for one convention
  // across the codebase rather than two for the same interaction primitive.
  const draggingRef = useRef(false);

  function handlePointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (e.button !== 0) return;
    draggingRef.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
  }

  function handlePointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (!draggingRef.current) return;
    const container = e.currentTarget.parentElement;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const next = (e.clientX - rect.left) / rect.width;
    onChange(Math.min(MAX_RATIO, Math.max(MIN_RATIO, next)));
  }

  function handlePointerUp(e: React.PointerEvent<HTMLDivElement>) {
    draggingRef.current = false;
    e.currentTarget.releasePointerCapture(e.pointerId);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      onChange(Math.max(MIN_RATIO, ratio - KEYBOARD_STEP));
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      onChange(Math.min(MAX_RATIO, ratio + KEYBOARD_STEP));
    }
  }

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize source and preview panes"
      aria-valuenow={Math.round(ratio * 100)}
      aria-valuemin={Math.round(MIN_RATIO * 100)}
      aria-valuemax={Math.round(MAX_RATIO * 100)}
      tabIndex={0}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onKeyDown={handleKeyDown}
      className="w-1.5 shrink-0 cursor-col-resize touch-none bg-ink-3 hover:bg-ink-4"
    />
  );
}
